import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SimpleGrid, useToast } from '@chakra-ui/react';
import {
  MdAgriculture,
  MdCancel,
  MdFileDownload,
  MdPendingActions,
} from 'react-icons/md';
import {
  listUserEquipment,
  exportUserEquipmentExcel,
} from '../../../repositories/userEquipment.repository';
import { lookupEquipmentTypes } from '../../../repositories/lookup.repository';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';
import EquipmentFleetDetailPage from '../equipments/fleet/detail';
import HubBuDeployedChart from './HubBuDeployedChart';
import HubFleetTypeChart, { CHART_SLICES } from './HubFleetTypeChart';
import {
  HubCard,
  HubError,
  HubFilterBar,
  HubGhostButton,
  HubKycBadge,
  HubLoading,
  HubMetricCard,
  HubPageHeader,
  HubTable,
  HubThumb,
} from './hubUi';

function matchesFleetSearch(asset, q) {
  if (!q) return true;
  const hay = [
    asset.assetId,
    asset.equipmentType,
    asset.regNo,
    asset.manufacturer,
    asset.model,
    asset.ownerName,
    asset.ownerPhone,
    asset.villageName,
    asset.status,
    asset.chassisNumber,
    asset.engineNumber,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export function HubEquipmentHub({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartSlice, setChartSlice] = useState('all');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getDashboardCounts());
    } catch (e) {
      setError(e.message || 'Failed to load equipment counts');
      toast({ title: e.message || 'Failed to load equipment counts', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const eq = data?.equipment || {};
  const slices = eq.byTypeSlices || {};
  const activeSlice =
    CHART_SLICES.find((s) => s.id === chartSlice) || CHART_SLICES[0];
  const chartByType =
    slices[activeSlice.id] ||
    (activeSlice.id === 'all' ? eq.byType : null) ||
    [];

  const sliceCounts = {
    all: eq.total ?? 0,
    available: eq.available ?? 0,
    deployed: eq.deployed ?? 0,
    pendingKyc: eq.pendingKyc ?? 0,
    rejected: eq.rejected ?? 0,
  };

  return (
    <>
      <HubPageHeader title="Equipment" />
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <HubMetricCard
          icon={MdAgriculture}
          label="All fleet"
          total={String(eq.total ?? 0)}
          metrics={[
            { label: 'Available', value: String(eq.available ?? 0) },
            { label: 'Deployed', value: String(eq.deployed ?? 0) },
            { label: 'Pending', value: String(eq.pendingKyc ?? 0) },
          ]}
          onClick={() => push({ id: 'equipmentList', label: 'Fleet list', filter: 'all' })}
        />
        <HubCard
          icon={MdPendingActions}
          label="Pending Verification"
          value={String(eq.pendingKyc ?? 0)}
          hint="Needs verification"
          onClick={() =>
            push({
              id: 'equipmentList',
              label: 'Pending Verification',
              filter: 'pending',
            })
          }
        />
        <HubCard
          icon={MdCancel}
          label="Rejected"
          value={String(eq.rejected ?? 0)}
          hint="Rejected verification"
          onClick={() =>
            push({ id: 'equipmentList', label: 'Rejected', filter: 'rejected' })
          }
        />
      </SimpleGrid>

      <SimpleGrid
        columns={{ base: 1, '2xl': 2 }}
        spacing={4}
        mt={5}
        alignItems="stretch"
      >
        <HubFleetTypeChart
          byType={chartByType}
          sliceLabel={activeSlice.label}
          chartSlice={chartSlice}
          sliceCounts={sliceCounts}
          onSliceChange={setChartSlice}
          onOpenAll={() =>
            push({
              id: 'equipmentList',
              label: activeSlice.label,
              filter: activeSlice.listFilter,
            })
          }
          onOpenType={(typeName) =>
            push({
              id: 'equipmentList',
              label: typeName,
              filter: activeSlice.listFilter,
              equipmentType: typeName,
            })
          }
        />
        <HubBuDeployedChart
          byBusinessUnit={eq.byBusinessUnitDeployed || []}
          onOpenIndents={() =>
            push({ id: 'indentList', label: 'Open indents', filter: 'open' })
          }
        />
      </SimpleGrid>
    </>
  );
}

export function HubEquipmentList({
  push,
  filter = 'all',
  userId,
  equipmentType = '',
}) {
  const toast = useToast();
  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(equipmentType || '');
  const [kycFilter, setKycFilter] = useState(
    filter === 'pending' ? 'Pending' : filter === 'rejected' ? 'Rejected' : '',
  );
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assetRes, typeRes] = await Promise.all([
        listUserEquipment({
          pageSize: 200,
          userId: userId || undefined,
        }),
        lookupEquipmentTypes(),
      ]);
      setAssets(assetRes.items || []);
      setTypes(typeRes || []);
    } catch (e) {
      toast({ title: e.message || 'Failed to load fleet', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setTypeFilter(equipmentType || '');
  }, [equipmentType]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((asset) => {
      if (filter === 'pending' && asset.status !== 'Pending') return false;
      if (filter === 'rejected' && asset.status !== 'Rejected') return false;
      const matchQ = matchesFleetSearch(asset, q);
      const matchType = !typeFilter || asset.equipmentType === typeFilter;
      const matchKyc = !kycFilter || (asset.status || 'Pending') === kycFilter;
      return matchQ && matchType && matchKyc;
    });
  }, [assets, search, typeFilter, kycFilter, filter]);

  const typeOptions = useMemo(() => {
    const names = Array.from(
      new Set(
        (types.length
          ? types.map((t) => t.name || t.code)
          : assets.map((a) => a.equipmentType)
        ).filter(Boolean),
      ),
    ).sort();
    return [
      { value: '', label: 'All types' },
      ...names.map((n) => ({ value: n, label: n })),
    ];
  }, [types, assets]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportUserEquipmentExcel({
        search: search.trim() || undefined,
        kycStatus: kycFilter
          ? kycFilter === 'Verified'
            ? 'verified'
            : kycFilter === 'Rejected'
              ? 'rejected'
              : 'pending'
          : undefined,
      });
      toast({ title: 'Export downloaded', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Export failed', status: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <HubPageHeader
        title="Fleet list"
        subtitle="Live equipment from the API. Search, filter, export — then open a row for full detail, photos, and documents."
        actions={
          <HubGhostButton
            leftIcon={<MdFileDownload />}
            onClick={handleExport}
            isLoading={exporting}
          >
            Export Excel
          </HubGhostButton>
        }
      />

      <HubFilterBar
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search reg, type, user, village…"
        filters={[
          {
            key: 'type',
            value: typeFilter,
            onChange: setTypeFilter,
            options: typeOptions,
          },
          {
            key: 'kyc',
            value: kycFilter,
            onChange: setKycFilter,
            options: [
              { value: '', label: 'All verification' },
              { value: 'Verified', label: 'Verified' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Rejected', label: 'Rejected' },
            ],
          },
        ]}
        onClear={() => {
          setSearch('');
          setTypeFilter('');
          setKycFilter('');
        }}
      />

      <HubTable
        loading={loading}
        rows={rows}
        onRowClick={(r) =>
          push({ id: 'equipmentDetail', label: r.regNo || r.assetId, itemId: r.id })
        }
        columns={[
          {
            key: 'thumb',
            label: '',
            render: (r) => <HubThumb src={r.thumbnailUrl} label={r.regNo} />,
          },
          { key: 'regNo', label: 'Reg no' },
          { key: 'equipmentType', label: 'Type' },
          {
            key: 'make',
            label: 'Make / model',
            render: (r) =>
              [r.manufacturer, r.model].filter(Boolean).join(' ') || '—',
          },
          { key: 'ownerName', label: 'User' },
          { key: 'villageName', label: 'Village' },
          {
            key: 'status',
            label: 'Verification',
            render: (r) => <HubKycBadge status={r.status}>{r.status}</HubKycBadge>,
          },
        ]}
      />
    </>
  );
}

/** Classic equipment detail UI embedded inside Hub. */
export function HubEquipmentDetail({ push, itemId }) {
  return (
    <EquipmentFleetDetailPage
      assetId={itemId}
      embedded
    />
  );
}
